<template>
  <div>
    <div class="qty-control-bar" elevation="5">
      <div class="qty-control-input-group">
        <v-text-field
          v-model="internalValue"
          :color="color"
          :messages="spareQtysMessages"
          :label="`${label}`"
          :rules="[ruleOk]"
          :readonly="readonly"
          @focus="inFocus = true; $event.target.select()"
          @blur="inFocus = false"
          inputmode="decimal"
          variant="underlined"
        >
          <template #message="{ message }">
            <v-chip size="small" block class="bg-primary text-white mt-1" v-if="message !== ' '">{{ message }}</v-chip>
            <v-chip block class="mt-1 bg-grey-lighten-4 text-black" label size="small">
              <div>
                <b>{{ internalValueDisplay }} {{ unit }}</b>
                <span v-for="spareQty in spareQtys" :key="spareQty"> + {{ spareQty }}</span>
              </div>
            </v-chip>
          </template>
          <template #append-inner>
            <span class="qty-total text-grey" v-if="qtyTotal">{{ qtyTotal }} {{ unit }}</span>
            <v-fade-transition leave-absolute>
              <span style="white-space:nowrap;" class="text-grey">
                <b style="font-weight: 400;"><template v-if="max">/ {{ max }}</template> <span>{{ unit }}</span></b>
              </span>
            </v-fade-transition>
          </template>
        </v-text-field>
      </div>
      <div class="qty-control-action-container">
          <v-card-actions class="qty-control-actions">
            <v-btn class="qty-control-button rounded-0" :disabled="internalValue < 1 || readonly" style="flex: auto;"
                   elevation="0" variant="outlined" selected-class="text-primary" color="primary" size="large" cols="12"
                   @click="internalValue -= 1">
              <v-icon>mdi-minus</v-icon>
            </v-btn>
            <v-btn class="qty-control-button rounded-0" elevation="0" style="flex: auto;"
                   :variant="!color ? 'outlined' : 'elevated'" size="large" cols="12" :color="color ?? 'primary'"
                   :disabled="!canIncrease" @click="internalValue += 1">
              <v-icon>
                mdi-plus
              </v-icon>
            </v-btn>
          </v-card-actions>
          <v-card-subtitle style="line-height:1.2;align-items: flex-start;"
                           class="show-ean py-1 px-2 text-grey text-center">
            <div class="ml-2">
              <small v-if="consistsOfDisplayed" class="mt-1">
                {{ consistsOfDisplayed }}
              </small>
              <small v-else class="mt-1">&nbsp;</small>
            </div>
          </v-card-subtitle>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {parseGermanFloat} from '@/util';
import {PropType, computed, ref} from 'vue';

const props = defineProps({
  modelValue: {
    type: Number,
    required: false,
    default: null,
  },
  max: {
    type: Number,
    required: false,
    default: null,
  },
  qtyTotal: {
    type: Number,
    required: false,
    default: null,
  },
  unit: {
    type: String,
    required: true,
  },
  spareQtys: {
    type: Array as PropType<Array<string | number>>,
    required: false,
    default: () => [],
  },
  ean: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  consistsOf: {
    type: String,
    required: false,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
  noZero: {
    type: Boolean,
    required: false,
    default: false,
  },
  allowOvershoot: {
    type: Boolean,
    default: false,
  },
});

const rounded = ref(true);
const inFocus = ref(false);

const emits = defineEmits({
  'update:modelValue': (value: number) => typeof value === 'number',
});

const internalValue = computed({
  get() {
    return props.modelValue;
  },
  set(newVal: number) {
    if (props.readonly) return;
    emits('update:modelValue', parseGermanFloat(newVal));
  },
});
const internalValueDisplay = computed({
  get() {
    return Math.floor(props.modelValue);
  },
  set(newVal: number | string) {
    internalValue.value = parseGermanFloat(newVal);
  },
});
const consistsOfDisplayed = computed(() => {
  return props.consistsOf ? `Inhalt: ${props.consistsOf}` : '';
});
const completed = computed(() => {
  return props.modelValue >= props.max;
});

const canIncrease = computed(() => {
  if (props.readonly) return false;
  return props.modelValue < props.max || props.allowOvershoot;
});

const spareQtysMessages = computed(() => {
  if (props.spareQtys.length > 0) return [' '];
  return [];
});

const ruleOk = (val: string | number): string | boolean => {
  val = parseGermanFloat(val);

  if (val > props.max && !props.allowOvershoot) return 'zu groß';
  if (val < 0) return 'zu klein';
  if (props.noZero && val === 0) return '0 ist nicht erlaubt';

  return true;
};
const color = computed(() => {
  if (props.max == 0) return undefined;
  if (internalValue.value === props.max) return 'success';
  if (internalValue.value > props.max) return 'warning';

  return undefined;
});

defineExpose({
  rounded,
  inFocus,
  internalValue,
  internalValueDisplay,
  consistsOfDisplayed,
  completed,
  spareQtysMessages,
  canIncrease,
  ruleOk,
  color,
});
</script>
<style scoped lang="scss">
.qty-control-bar {
  display: flex;
  margin-bottom: 1rem;
}

.qty-control-actions {
  padding: 5px 0 6px 6px;

  .v-btn {
    padding: 0;
  }
}

.qty-control-input-group {
  position: relative;
  flex: auto;
}

.qty-control-spare {
  color: #fff;
  width: 100%;
  border-top-right-radius: 0 !important;
  border-top-left-radius: 0 !important;
}

.v-text-field--filled {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.v-messages__message {
  font-weight: bold;
}

.qty-total {
  position: absolute;
  top: 0;
  right: 8px;
  font-size: 13px;
}

.qty-control-action-container {
  width: 7rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  .show-ean {
    position: relative;
    width: 7rem;
    overflow-x: visible;
    text-align: right !important;

    small {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }

  .qty-control-actions {
    display: flex;
    width: 100%;

    .qty-control-button {
      min-width: auto !important;
      flex: auto;
    }
  }
}
</style>


<style lang="scss">
.qty-control-input-group .v-input__append-inner {
  margin-top: auto !important;
  margin-bottom: 8px !important;
  margin-right: 8px;
}

.qty-control-input-group .v-input__slot {
  margin-bottom: 0;
}

.qty-control-bar .v-chip {
  border-radius: 0 !important;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>
